import { FormulaError } from './errors.js';
import type { Token } from './lexer.js';
import { tokenize } from './lexer.js';

export type ASTNode =
  | { type: 'number'; value: number }
  | { type: 'string'; value: string }
  | { type: 'boolean'; value: boolean }
  | { type: 'field_ref'; slug: string }
  | {
      type: 'binary';
      op: '+' | '-' | '*' | '/' | '=' | '!=' | '>' | '>=' | '<' | '<=';
      left: ASTNode;
      right: ASTNode;
    }
  | { type: 'unary'; op: '-'; operand: ASTNode }
  | { type: 'call'; name: string; args: ASTNode[] };

type BinaryOp = Extract<ASTNode, { type: 'binary' }>['op'];

class Parser {
  private pos = 0;

  constructor(private tokens: Token[]) {}

  private peek(): Token {
    return this.tokens[this.pos]!;
  }

  private advance(): Token {
    const t = this.tokens[this.pos]!;
    this.pos++;
    return t;
  }

  private expect(type: string): Token {
    const t = this.peek();
    if (t.type !== type) {
      throw new FormulaError(
        `Expected ${type}, got ${t.type} ('${t.value}')`,
        'syntax_error',
        t.position,
      );
    }
    return this.advance();
  }

  parse(): ASTNode {
    const node = this.parseComparison();
    if (this.peek().type !== 'EOF') {
      const t = this.peek();
      throw new FormulaError(
        `Unexpected token: '${t.value}'`,
        'syntax_error',
        t.position,
      );
    }
    return node;
  }

  // Precedence 1: comparisons (=, !=, >, >=, <, <=)
  private parseComparison(): ASTNode {
    let left = this.parseAddition();

    while (true) {
      const t = this.peek();
      if (
        t.type === 'EQ' ||
        t.type === 'NEQ' ||
        t.type === 'GT' ||
        t.type === 'GTE' ||
        t.type === 'LT' ||
        t.type === 'LTE'
      ) {
        const op = this.advance().value as BinaryOp;
        const right = this.parseAddition();
        left = { type: 'binary', op, left, right };
      } else {
        break;
      }
    }

    return left;
  }

  // Precedence 2: addition (+, -)
  private parseAddition(): ASTNode {
    let left = this.parseMultiplication();

    while (true) {
      const t = this.peek();
      if (t.type === 'PLUS' || t.type === 'MINUS') {
        const op = this.advance().value as '+' | '-';
        const right = this.parseMultiplication();
        left = { type: 'binary', op, left, right };
      } else {
        break;
      }
    }

    return left;
  }

  // Precedence 3: multiplication (*, /)
  private parseMultiplication(): ASTNode {
    let left = this.parseUnary();

    while (true) {
      const t = this.peek();
      if (t.type === 'STAR' || t.type === 'SLASH') {
        const op = this.advance().value as '*' | '/';
        const right = this.parseUnary();
        left = { type: 'binary', op, left, right };
      } else {
        break;
      }
    }

    return left;
  }

  // Precedence 4: unary (-)
  private parseUnary(): ASTNode {
    const t = this.peek();
    if (t.type === 'MINUS') {
      this.advance();
      const operand = this.parseUnary();
      return { type: 'unary', op: '-', operand };
    }
    return this.parsePrimary();
  }

  // Precedence 5: primaries
  private parsePrimary(): ASTNode {
    const t = this.peek();

    // Number
    if (t.type === 'NUMBER') {
      this.advance();
      return { type: 'number', value: parseFloat(t.value) };
    }

    // String
    if (t.type === 'STRING') {
      this.advance();
      return { type: 'string', value: t.value };
    }

    // Boolean
    if (t.type === 'TRUE') {
      this.advance();
      return { type: 'boolean', value: true };
    }
    if (t.type === 'FALSE') {
      this.advance();
      return { type: 'boolean', value: false };
    }

    // Field reference
    if (t.type === 'FIELD_REF') {
      this.advance();
      return { type: 'field_ref', slug: t.value };
    }

    // Function call: IDENT LPAREN args RPAREN
    if (t.type === 'IDENT') {
      this.advance();
      const name = t.value;
      this.expect('LPAREN');
      const args: ASTNode[] = [];

      if (this.peek().type !== 'RPAREN') {
        // SCALE second arg is [[t,r],[t,r],...] — parse as special nested array
        if (name.toUpperCase() === 'SCALE') {
          args.push(this.parseComparison());
          this.expect('COMMA');
          args.push(this.parseScaleThresholds());
        } else {
          args.push(this.parseComparison());
          while (this.peek().type === 'COMMA') {
            this.advance(); // skip comma
            args.push(this.parseComparison());
          }
        }
      }

      this.expect('RPAREN');
      return { type: 'call', name: name.toUpperCase(), args };
    }

    // Grouped expression
    if (t.type === 'LPAREN') {
      this.advance();
      const expr = this.parseComparison();
      this.expect('RPAREN');
      return expr;
    }

    throw new FormulaError(
      `Unexpected token: '${t.value}'`,
      'syntax_error',
      t.position,
    );
  }

  /**
   * Parse SCALE thresholds: [[t1,r1],[t2,r2],...]
   * We represent this as a call node named '__SCALE_THRESHOLDS__'
   * where each arg is a call node '__SCALE_PAIR__' with two number args.
   *
   * Actually, let's keep it simpler: represent as a single ASTNode[] embedded
   * in a special node. We'll use a call node with name '__thresholds__' and
   * pairs as children.
   */
  private parseScaleThresholds(): ASTNode {
    this.expect('LBRACKET'); // outer [
    const pairs: ASTNode[] = [];

    while (this.peek().type !== 'RBRACKET') {
      if (pairs.length > 0) {
        this.expect('COMMA');
      }
      this.expect('LBRACKET'); // inner [
      const threshold = this.parseComparison();
      this.expect('COMMA');
      const result = this.parseComparison();
      this.expect('RBRACKET'); // inner ]
      // Store pair as a call node
      pairs.push({ type: 'call', name: '__PAIR__', args: [threshold, result] });
    }

    this.expect('RBRACKET'); // outer ]
    return { type: 'call', name: '__THRESHOLDS__', args: pairs };
  }
}

/**
 * Parse a formula string into an AST.
 */
export function parse(input: string): ASTNode {
  const tokens = tokenize(input);
  const parser = new Parser(tokens);
  return parser.parse();
}
